<template>
  <div>
    <!-- TOP ACTION BAR (ONLY FOR COURSE) -->
    <div
      class="d-flex justify-content-between align-items-center mb-2"
      v-if="course_id"
    >
      <h6 class="mb-0">Programme Specific Outcome Groups</h6>

      <div>
        <!-- EDIT ICON -->
        <i
          v-if="!editMode"
          class="fas fa-edit text-info"
          style="cursor:pointer;font-size:18px"
          @click="enableEditMode"
        ></i>

        <!-- SAVE / CANCEL -->
        <div v-else>
          <b-button
            size="sm"
            variant="success"
            class="mr-2"
            @click="confirmSavePsoGroup"
          >
            Save
          </b-button>

          <b-button size="sm" variant="secondary" @click="cancelEditMode">
            Cancel
          </b-button>
        </div>
      </div>
    </div>

    <!-- CONFIRM MODAL -->
    <b-modal
      id="confirmSavePsoGroupModal"
      hide-header
      hide-footer
      centered
      size="sm"
    >
      <h6 class="text-danger font-weight-bold">
        Confirm PSO Group Change
      </h6>

      <p class="mt-2 mb-2 text-danger">
        All PSO mapping including CO-PO-PSO mapping, Lesson Plan and activity data will be
        deleted when the PSO Group is changed.
      </p>

      <p class="mb-2">
        Type <strong>SWAP</strong> to confirm.
      </p>

      <b-form-input
        v-model="swapConfirmText"
        placeholder="Type SWAP to continue"
        class="mb-3"
      />

      <div class="text-right">
        <b-button
          size="sm"
          variant="secondary"
          @click="
            $bvModal.hide('confirmSavePsoGroupModal');
            selectedPsoGroupId = originalPsoGroupId;
            swapConfirmText = '';
          "
        >
          Cancel
        </b-button>

        <b-button
          size="sm"
          variant="success"
          class="ml-2"
          :disabled="swapConfirmText !== 'SWAP'"
          @click="proceedSavePsoGroup"
        >
          Yes, Save
        </b-button>
      </div>
    </b-modal>

    <!-- ADD PSO GROUP (NON-COURSE) -->
    <div class="mb-3">
      <b-button
        variant="primary"
        v-if="!course_id"
        @click="openAddGroupModal"
      >
        Add PSO Group
      </b-button>
    </div>

    <div class="groupData">
      <b-overlay
        :show="groupLoading"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="md"
      >
        <table class="table table-bordered table-striped">
          <thead class="thead-dark">
            <tr>
              <th v-if="course_id" style="width:40px"></th>
              <th>Group Name</th>
              <th>PSO</th>
              <th v-if="!course_id"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="group in groups">
              <!-- GROUP ROW -->
              <tr
                :key="group.id"
                v-if="!activeGroupId || activeGroupId === group.id"
              >
                <td
                  v-if="course_id"
                  class="text-center align-middle"
                >
                  <b-form-radio
                    v-model="selectedPsoGroupId"
                    :value="group.id"
                    name="psoGroup"
                    :disabled="!editMode"
                  />
                </td>

                <td class="align-middle">
                  {{ group.name }}
                </td>

                <td class="align-middle">
                  <span
                    class="text-info btn p-0 m-0 font-weight-bolder"
                    @click="togglePsoDashboard(group.id)"
                  >
                    {{ group.programme_specific_outcomes.length }} PSO Added
                  </span>
                </td>

                <td v-if="!course_id" class="align-middle">
                  <i
                    class="fas fa-edit text-info"
                    style="cursor:pointer;font-size:18px"
                    @click="openEditGroupModal(group)"
                  ></i>
                </td>
              </tr>

              <!-- PSO DETAILS -->
              <tr
                v-if="activeGroupId === group.id"
                :key="'pso-' + group.id"
              >
                <td :colspan="course_id ? 3 : 2" class="bg-light">
                  <usergroup-pso-dashboard
                    :usergroupId="usergroupId"
                    :pso_group_id="group.id"
                    :course_id="course_id"
                    @refresh-groups="fetchGroups"
                  />
                </td>
              </tr>
            </template>

            <tr v-if="!groups.length && !groupLoading">
              <td
                :colspan="course_id ? 3 : 2"
                class="text-center text-muted"
              >
                No groups found
              </td>
            </tr>
          </tbody>
        </table>
      </b-overlay>
    </div>

    <!-- ADD / EDIT MODAL -->
    <b-modal v-model="showAddGroupModal" :title="modalTitle" hide-footer>
      <b-form @submit.prevent="submitPsoGroup">
        <b-form-group label="Group Name">
          <b-form-input v-model="groupName" required />
        </b-form-group>

        <div class="text-right">
          <b-button variant="secondary" @click="closeAddGroupModal">
            Cancel
          </b-button>
          <b-button variant="primary" type="submit" class="ml-2">
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import UsergroupPsoDashboard from "./UsergroupPsoDashboard.vue";

export default {
  props: ["course_id", "usergroupId"],

  components: { UsergroupPsoDashboard },

  data() {
    return {
      groups: [],
      groupLoading: false,

      showAddGroupModal: false,
      groupName: "",
      editGroupId: null,

      activeGroupId: null,

      // COURSE MODE
      editMode: false,
      selectedPsoGroupId: null,
      originalPsoGroupId: null,

      swapConfirmText: "",
    };
  },

  computed: {
    modalTitle() {
      return this.editGroupId ? "Edit PSO Group" : "Add PSO Group";
    }
  },

  created() {
    this.fetchGroups();
  },

  methods: {
    confirmSavePsoGroup() {
      if (this.selectedPsoGroupId === this.originalPsoGroupId) {
        this.editMode = false;
        return;
      }
      this.$bvModal.show("confirmSavePsoGroupModal");
    },

    async proceedSavePsoGroup() {
      this.$bvModal.hide("confirmSavePsoGroupModal");
      this.swapConfirmText = "";
      await this.saveCoursePsoGroup();
    },

    async fetchGroups() {
      this.groupLoading = true;
      try {
        const url =
          this.$store.getters.getAPIKey.mainAPI +
          `/pso-groups?usergroup_id=${this.usergroupId}&course_id=${this.course_id}`;

        const res = await this.$axios.get(url);

        this.groups = res.data.groups;
        this.selectedPsoGroupId = res.data.course_property_pso_group_id;
        this.originalPsoGroupId = res.data.course_property_pso_group_id;
      } finally {
        this.groupLoading = false;
      }
    },

    enableEditMode() {
      this.editMode = true;
    },

    cancelEditMode() {
      this.selectedPsoGroupId = this.originalPsoGroupId;
      this.editMode = false;
    },

    async saveCoursePsoGroup() {
      await this.$axios.post(
        this.$store.getters.getAPIKey.mainAPI + "/pso-groups/course-pso-group",
        {
          course_id: this.course_id,
          pso_group_id: this.selectedPsoGroupId
        }
      );

      this.originalPsoGroupId = this.selectedPsoGroupId;
      this.editMode = false;

      this.$toast.success("PSO Group updated successfully", {
        position: "top",
        duration: 3000,
      });
    },

    openAddGroupModal() {
      this.groupName = "";
      this.editGroupId = null;
      this.showAddGroupModal = true;
    },

    openEditGroupModal(group) {
      this.groupName = group.name;
      this.editGroupId = group.id;
      this.showAddGroupModal = true;
    },

    closeAddGroupModal() {
      this.showAddGroupModal = false;
      this.groupName = "";
      this.editGroupId = null;
    },

    async submitPsoGroup() {
      if (!this.groupName) return;

      const payload = {
        name: this.groupName,
        usergroup_id: this.usergroupId
      };

      if (this.editGroupId) {
        await this.$axios.put(
          this.$store.getters.getAPIKey.mainAPI +
            `/pso-groups/${this.editGroupId}`,
          payload
        );
      } else {
        await this.$axios.post(
          this.$store.getters.getAPIKey.mainAPI + "/pso-groups",
          payload
        );
      }

      this.closeAddGroupModal();
      this.fetchGroups();
    },

    togglePsoDashboard(groupId) {
      this.activeGroupId =
        this.activeGroupId === groupId ? null : groupId;
    }
  }
};
</script>
